import React from 'react';
import { Box, Button, Dialog, Typography, CircularProgress } from '@mui/material';
import DynamicTable, { Column } from './table';

export type ProvisionTransactionRow = {
    id: string;
    date: string;
    amount: number | string;
    description: string;
    category: string;
    bank: string;
    type: 'real' | 'provision';
};

interface ProvisionTransactionsDisplayProps {
    open: boolean;
    provisionId?: number | null;
    transactions?: any[];
    banks?: any[];
    categories?: any[];
    onConfirm?: (trxGuids: string[]) => void;
    onClose?: () => void;
    isLoading?: boolean;
}

// Stable references for the default props below - a literal `[]` in a default
// parameter is re-created on every render whenever the caller omits the prop
// (e.g. transaction-detail.tsx doesn't pass banks/categories), which changes
// the effect's dependency identity every render and loops forever (React
// error #185, Maximum update depth exceeded) once it calls setData([]) with
// yet another new empty array each time.
const EMPTY_ARRAY: any[] = [];

const ProvisionTransactionsDisplay: React.FC<ProvisionTransactionsDisplayProps> = ({
    open = false,
    provisionId,
    transactions = EMPTY_ARRAY,
    banks = EMPTY_ARRAY,
    categories = EMPTY_ARRAY,
    onConfirm,
    onClose,
    isLoading = false,
}) => {
    const [data, setData] = React.useState<ProvisionTransactionRow[]>([]);
    const [selectedIndices, setSelectedIndices] = React.useState<number[]>([]);
    const [selectedTransactionIds, setSelectedTransactionIds] = React.useState<string[]>([]);

    // Transform provided transactions data
    React.useEffect(() => {
        if (transactions && transactions.length > 0) {
            const transformedData = transactions.map((transaction: any) => {
                // Lookup bank name from bankAccountId
                let bankName = 'No Bank';
                if (transaction.bankAccountId) {
                    const bank = banks.find(b => b.id === transaction.bankAccountId);
                    if (bank) {
                        bankName = bank.bank && bank.name ? `${bank.bank} (${bank.name})` : bank.bank || bank.name || `Bank ID: ${transaction.bankAccountId}`;
                    } else {
                        bankName = `Bank ID: ${transaction.bankAccountId}`;
                    }
                } else if (transaction.bankName) {
                    bankName = transaction.bankName;
                } else if (transaction.bank?.name) {
                    bankName = transaction.bank.name;
                }
                
                // Lookup category name from categoryId
                let categoryName = 'Uncategorized';
                if (transaction.categoryId) {
                    const findCategory = (cats: any[]): any => {
                        for (const cat of cats) {
                            if (cat.id === transaction.categoryId) return cat;
                            if (cat.children && cat.children.length > 0) {
                                const found = findCategory(cat.children);
                                if (found) return found;
                            }
                        }
                        return null;
                    };
                    const category = findCategory(categories);
                    categoryName = category?.name || `Category ID: ${transaction.categoryId}`;
                } else if (transaction.categoryName) {
                    categoryName = transaction.categoryName;
                } else if (transaction.category?.name) {
                    categoryName = transaction.category.name;
                }
                
                return {
                    id: transaction.id || transaction.guid || '',
                    date: transaction.date || transaction.transactionDate || '',
                    amount: transaction.amount || 0,
                    description: transaction.description || '',
                    category: categoryName,
                    bank: bankName,
                    type: transaction.type || 'real'
                };
            });
            setData(transformedData);
            
            // Default to all selected
            const allIndices = Array.from({ length: transformedData.length }, (_, idx) => idx);
            setSelectedIndices(allIndices);
            setSelectedTransactionIds(transformedData.map(t => t.id));
        } else {
            setData([]);
            setSelectedIndices([]);
            setSelectedTransactionIds([]);
        }
    }, [transactions, banks, categories]);

    // Handle selection changes
    const handleSelectionChange = (indices: number[]) => {
        setSelectedIndices(indices);
        setSelectedTransactionIds(
            indices.map(idx => data[idx].id)
        );
    };

    const columns: Column[] = [
        { 
            id: 'select', 
            label: 'Select', 
            size: 25, 
            type: 'checkbox' 
        },
        { 
            id: 'date', 
            label: 'Date', 
            size: 100, 
            type: 'date'
        },
        { 
            id: 'amount', 
            label: 'Amount', 
            size: 100, 
            type: 'currency'
        },
        { 
            id: 'description', 
            label: 'Description', 
            size: 250
        },
        // { 
        //     id: 'category', 
        //     label: 'Category', 
        //     size: 150
        // },
        // { 
        //     id: 'bank', 
        //     label: 'Bank', 
        //     size: 150
        // },
    ];

    return (
        <Dialog
            open={open && !!provisionId}
            maxWidth="md"
            onClose={(event, reason) => {
                if (reason === 'backdropClick') return;
                onClose?.();
            }}
            disableEscapeKeyDown={true}
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                }
            }}
        >
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: '90vh' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, flexShrink: 0 }}>
                    Confirm Related Transactions
                </Typography>
                <Typography variant="body2" gutterBottom sx={{ color: 'text.secondary', mb: 2, fontSize: 12, flexShrink: 0 }}>
                    Below are the transactions related to this provision. Select the transactions that you want to associate to this provision. These transactions will inherit the provision template's category, but you can edit them later if needed.
                </Typography>
                <Box sx={{ flex: 1, overflow: 'auto', mb: 2, minHeight: 0 }}>
                    <DynamicTable
                        columns={columns}
                        data={data}
                        isLoading={false}
                        contained={false}
                        maxHeight={400}
                        selectedIndices={selectedIndices}
                        onSelectionChange={handleSelectionChange}
                    />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 2, borderTop: '1px solid #eee', flexShrink: 0 }}>
                    <Button
                        onClick={() => onConfirm?.(selectedTransactionIds)}
                        variant="contained"
                        disabled={isLoading}
                        sx={{
                            backgroundColor: 'var(--color-primary)',
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                backgroundColor: '#009178',
                            },
                        }}
                    >
                        {isLoading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={20} sx={{ color: 'white' }} />
                                Processing...
                            </Box>
                        ) : (
                            'Confirm'
                        )}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default ProvisionTransactionsDisplay;
